import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  Pressable,
  TextInput,
  Platform,
  ActivityIndicator,
} from "react-native";
import {
  CalendarDays,
  Mail,
  MapPin,
  Mars,
  Phone,
  Send,
  UserRound,
  Venus,
  VenusAndMars,
  X,
} from "lucide-react-native";
import {
  AddressField,
  ActionBanner,
  type AddressData,
  colors,
  semanticColors,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  MoonPhoneField,
  Dialog,
  DialogPrimaryButton,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../../onboarding/theme";
import type { PersonalMedicalData } from "../types";
import { useYouScreen } from "../YouScreenContext";
import {
  EmailVerificationBadge,
  useEmailVerificationStatus,
  usePhoneVerification,
  usePhoneVerificationStatus,
} from "../EmailVerificationBadge";
import { useStytch, useStytchUser } from "../../../../utils/useStytch";
import {
  usePatient,
  useUpdatePatient,
  type UpdatePatientInput,
} from "@src/packages/patients/hooks/usePatient";
import {
  getPatientGoal,
  type TreatmentGoal,
} from "@src/packages/patients/patients.types";
import { resolveSexAtBirthGoalImpact } from "@src/packages/persona/resolveSexAtBirthGoalImpact";
import { useSendBetaInvite } from "../../../../betaInvite/useSendBetaInvite";
import { getAuthCallbackUrl } from "../../../../utils/getAuthCallbackUrl";

const SEX_OPTIONS = [
  {
    labelKey: "you.personal_info.sex_female",
    value: "AFAB",
    Icon: Venus,
  },
  {
    labelKey: "you.personal_info.sex_male",
    value: "AMAB",
    Icon: Mars,
  },
] as const;

type SexAtBirth = (typeof SEX_OPTIONS)[number]["value"];

type Props = {
  data: PersonalMedicalData;
  initialPhone?: string;
  /** Close after Save or Cancel; drafts are intentionally local until Save. */
  onClose: () => void;
};

type SaveIssue = {
  variant: "important" | "warning";
  message: string;
  action?: "review-goal";
};

type SaveTask = {
  key?: "address" | "email-clear" | "phone-clear";
  label: string;
  save: () => Promise<void>;
};

function getGoalLabel(
  t: TranslationFunction,
  goal: TreatmentGoal | null,
): string | null {
  switch (goal) {
    case "egg-freezing":
    case "egg-preserve":
      return t("you.my_journey.intent_egg_freezing");
    case "sperm-freezing":
    case "sperm-preserve":
      return t("you.my_journey.intent_sperm_freezing");
    case "embryo-freezing":
    case "embryo-preserve":
      return t("you.my_journey.intent_embryo_freezing");
    case "pregnancy":
      return t("you.my_journey.intent_pregnancy");
    case "egg-donation":
      return t("you.my_journey.intent_egg_donation");
    case "sperm-donation":
      return t("you.my_journey.intent_sperm_donation");
    case "adoption":
      return t("you.my_journey.intent_adoption");
    case "surrogacy":
      return t("you.my_journey.intent_surrogacy");
    case "exploring":
      return t("you.my_journey.intent_exploring");
    default:
      return null;
  }
}

export default function PersonalInfo({ data, initialPhone, onClose }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PersonalInfo");
  const { dispatch } = useYouScreen();
  const stytch = useStytch();
  const { user: stytchUser } = useStytchUser();
  const updatePatient = useUpdatePatient();
  const { data: patient } = usePatient();

  useEffect(() => {
    if (!patient || patient.phoneNumber) return;
    const stytchPhone = stytchUser?.phone_numbers?.[0]?.phone_number;
    if (!stytchPhone) return;
    updatePatient.mutate({ phoneNumber: stytchPhone });
  }, [patient, stytchUser, updatePatient]);

  // This editor is intentionally draft-based: blur and disclosure controls
  // never persist data. Save applies the complete edit; Cancel simply closes.
  const [firstName, setFirstName] = useState(data.firstName ?? "");
  const [lastName, setLastName] = useState(data.lastName ?? "");
  const [email, setEmail] = useState(data.email ?? "");
  const [phone, setPhone] = useState(data.phone ?? initialPhone ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(data.dateOfBirth ?? "");
  const [draftSexAtBirth, setDraftSexAtBirth] = useState<SexAtBirth | null>(
    isSexAtBirth(data.gender) ? data.gender : null,
  );
  const [addressDraft, setAddressDraft] = useState<AddressData | null>(null);
  const [emailClearRequested, setEmailClearRequested] = useState(false);
  const [phoneClearRequested, setPhoneClearRequested] = useState(false);
  const [pendingSexAtBirth, setPendingSexAtBirth] = useState<SexAtBirth | null>(
    null,
  );
  const [goalReviewSexAtBirth, setGoalReviewSexAtBirth] =
    useState<SexAtBirth | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveIssue, setSaveIssue] = useState<SaveIssue | null>(null);
  const [emailVerificationError, setEmailVerificationError] = useState<
    string | null
  >(null);
  const savingRef = useRef(false);
  const removedEmailFactorIdRef = useRef<string | null>(null);
  const removedPhoneFactorIdRef = useRef<string | null>(null);

  const emailHasUnsavedChanges = toNullable(email) !== toNullable(data.email);
  const emailForVerification = emailHasUnsavedChanges
    ? null
    : toNullable(data.email);
  const emailVerificationStatus =
    useEmailVerificationStatus(emailForVerification);
  const phoneHasUnsavedChanges = toNullable(phone) !== toNullable(data.phone);
  // Verification must always target the saved number. In particular, never
  // leave a Verify button for the previous number visible while the user has
  // typed a replacement that has not been saved yet.
  const phoneForVerification = phoneHasUnsavedChanges
    ? null
    : toNullable(data.phone);
  const phoneVerificationStatus =
    usePhoneVerificationStatus(phoneForVerification);
  const emailVerified = emailVerificationStatus === "verified";
  const phoneVerified = phoneVerificationStatus === "verified";
  // A login factor can only be removed if another verified factor remains.
  const totalFactors =
    (stytchUser?.emails.length ?? 0) + (stytchUser?.phone_numbers.length ?? 0);
  const canRemoveEmail = emailVerified && totalFactors > 1;
  const canRemovePhone = phoneVerified && totalFactors > 1;
  const phoneVerification = usePhoneVerification({
    status: phoneVerificationStatus,
    phone: phoneForVerification ?? undefined,
  });
  const showPhoneVerification = !phoneVerified && !!phoneForVerification;

  useEffect(() => {
    // Each successful field save optimistically refreshes `data`. Do not
    // replace the remaining draft values while a partial save is in flight.
    if (savingRef.current || goalReviewSexAtBirth) return;
    setFirstName(data.firstName ?? "");
    setLastName(data.lastName ?? "");
    setEmail(data.email ?? "");
    setPhone(data.phone ?? initialPhone ?? "");
    setDateOfBirth(data.dateOfBirth ?? "");
    setDraftSexAtBirth(isSexAtBirth(data.gender) ? data.gender : null);
    setAddressDraft(null);
    setEmailClearRequested(false);
    setPhoneClearRequested(false);
    setPendingSexAtBirth(null);
    setEmailVerificationError(null);
  }, [data, goalReviewSexAtBirth, initialPhone]);

  const savedSexAtBirth = isSexAtBirth(data.gender) ? data.gender : null;
  const currentGoal = patient ? getPatientGoal(patient) : null;
  const currentGoalLabel = getGoalLabel(t, currentGoal);
  const draftSexAtBirthGoalImpact = resolveSexAtBirthGoalImpact(
    currentGoal,
    draftSexAtBirth,
  );
  const pendingSexAtBirthGoalImpact = pendingSexAtBirth
    ? resolveSexAtBirthGoalImpact(currentGoal, pendingSexAtBirth)
    : null;
  const sexAtBirthChangeDescription =
    pendingSexAtBirthGoalImpact?.kind === "review" && currentGoalLabel
      ? t("you.personal_info.sex_at_birth_change_goal_conflict_description", {
          params: { goal: currentGoalLabel },
        })
      : pendingSexAtBirthGoalImpact?.kind === "recalculate" && currentGoalLabel
        ? t("you.personal_info.sex_at_birth_change_goal_update_description", {
            params: { goal: currentGoalLabel },
          })
        : t(
            "you.personal_info.sex_at_birth_change_personalization_description",
          );
  const hasSexAtBirthGoalConflict =
    draftSexAtBirth !== savedSexAtBirth &&
    draftSexAtBirthGoalImpact.kind === "review";

  const hasChanges =
    toNullable(firstName) !== toNullable(data.firstName) ||
    toNullable(lastName) !== toNullable(data.lastName) ||
    toNullable(email) !== toNullable(data.email) ||
    toNullable(phone) !== toNullable(data.phone) ||
    toNullable(dateOfBirth) !== toNullable(data.dateOfBirth) ||
    draftSexAtBirth !== savedSexAtBirth ||
    addressDraft !== null ||
    emailClearRequested ||
    phoneClearRequested;

  const selectSexAtBirth = useCallback(
    (next: SexAtBirth) => {
      if (next === draftSexAtBirth) return;

      // A saved answer is a correction, and a first answer can still affect an
      // existing fertility goal. Ask for confirmation before letting either
      // change enter the draft; Save still controls persistence.
      if ((savedSexAtBirth || currentGoal) && next !== savedSexAtBirth) {
        setPendingSexAtBirth(next);
        return;
      }

      setDraftSexAtBirth(next);
    },
    [currentGoal, draftSexAtBirth, savedSexAtBirth],
  );

  const confirmSexAtBirthChange = useCallback(() => {
    if (!pendingSexAtBirth) return;
    setDraftSexAtBirth(pendingSexAtBirth);
    setSaveIssue(null);
    setPendingSexAtBirth(null);
  }, [pendingSexAtBirth]);

  const openGoalReviewForSexAtBirth = useCallback(
    (nextSexAtBirth: SexAtBirth) => {
      setDraftSexAtBirth(nextSexAtBirth);
      setGoalReviewSexAtBirth(nextSexAtBirth);
      setPendingSexAtBirth(null);
      setSaveIssue(null);
      dispatch({
        type: "SET_INTENT_PICKER_GENDER_OVERRIDE",
        gender: nextSexAtBirth,
      });
      dispatch({ type: "SHOW_MODAL", modal: "intent-picker" });
    },
    [dispatch],
  );

  const reviewGoalForSexAtBirthChange = useCallback(() => {
    if (!draftSexAtBirth) return;
    openGoalReviewForSexAtBirth(draftSexAtBirth);
  }, [draftSexAtBirth, openGoalReviewForSexAtBirth]);

  const reviewGoalFromSexAtBirthDialog = useCallback(() => {
    if (!pendingSexAtBirth) return;
    openGoalReviewForSexAtBirth(pendingSexAtBirth);
  }, [openGoalReviewForSexAtBirth, pendingSexAtBirth]);

  const confirmSexAtBirthChangeFromDialog = useCallback(() => {
    if (pendingSexAtBirthGoalImpact?.kind === "review") {
      reviewGoalFromSexAtBirthDialog();
      return;
    }
    confirmSexAtBirthChange();
  }, [
    confirmSexAtBirthChange,
    pendingSexAtBirthGoalImpact?.kind,
    reviewGoalFromSexAtBirthDialog,
  ]);

  const sexAtBirthChangeConfirmLabel =
    pendingSexAtBirthGoalImpact?.kind === "review"
      ? t("you.personal_info.sex_at_birth_goal_review_action")
      : t("you.personal_info.sex_at_birth_change_confirm");

  const requestClearEmail = useCallback(() => {
    setEmail("");
    setEmailClearRequested(true);
    setEmailVerificationError(null);
  }, []);

  const requestClearPhone = useCallback(() => {
    setPhone("");
    setPhoneClearRequested(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasChanges || isSaving) return;
    if (!patient) {
      setSaveIssue({
        variant: "important",
        message: t("you.personal_info.profile_not_ready_message"),
      });
      return;
    }
    if (hasSexAtBirthGoalConflict && currentGoalLabel) {
      setSaveIssue({
        variant: "important",
        action: "review-goal",
        message: t(
          "you.personal_info.sex_at_birth_goal_conflict_save_message",
          { params: { goal: currentGoalLabel } },
        ),
      });
      return;
    }

    const nextFirstName = toNullable(firstName);
    const nextLastName = toNullable(lastName);
    const nextEmail = toNullable(email);
    const nextPhone = toNullable(phone);
    const nextDateOfBirth = toNullable(dateOfBirth);
    const savedFirstName = toNullable(data.firstName);
    const savedLastName = toNullable(data.lastName);
    const savedEmail = toNullable(data.email);
    const savedPhone = toNullable(data.phone);
    const savedDateOfBirth = toNullable(data.dateOfBirth);
    const tasks: SaveTask[] = [];
    let emailVerificationFailed = false;

    const addPatientTask = (
      label: string,
      payload: UpdatePatientInput,
      key?: SaveTask["key"],
    ) => {
      tasks.push({
        key,
        label,
        save: async () => {
          await updatePatient.mutateAsync(payload);
        },
      });
    };

    if (nextFirstName !== savedFirstName) {
      addPatientTask(t("you.personal_info.first_name_label"), {
        firstName: nextFirstName,
      });
    }
    if (nextLastName !== savedLastName) {
      addPatientTask(t("you.personal_info.last_name_label"), {
        lastName: nextLastName,
      });
    }

    if (emailClearRequested && savedEmail) {
      tasks.push({
        key: "email-clear",
        label: t("you.personal_info.email_label"),
        save: async () => {
          const stytchEmail = stytchUser?.emails.find(
            (item) => item.email.toLowerCase() === savedEmail.toLowerCase(),
          );
          if (
            stytchEmail &&
            removedEmailFactorIdRef.current !== stytchEmail.email_id
          ) {
            await stytch.user.deleteEmail(stytchEmail.email_id);
            removedEmailFactorIdRef.current = stytchEmail.email_id;
          }
          await updatePatient.mutateAsync({ email: "" });
        },
      });
    } else if (nextEmail !== savedEmail) {
      tasks.push({
        label: t("you.personal_info.email_label"),
        save: async () => {
          await updatePatient.mutateAsync({ email: nextEmail ?? "" });
          if (!nextEmail) return;

          try {
            await stytch.magicLinks.email.send(nextEmail, {
              login_magic_link_url: getAuthCallbackUrl(),
              signup_magic_link_url: getAuthCallbackUrl(),
            });
          } catch {
            // The profile change itself succeeded. Keep the modal open and
            // direct the user to the inline Verify affordance to retry.
            emailVerificationFailed = true;
          }
        },
      });
    }

    if (phoneClearRequested && savedPhone) {
      tasks.push({
        key: "phone-clear",
        label: t("you.personal_info.phone_label"),
        save: async () => {
          const normalized = savedPhone.replace(/[^\d+]/g, "");
          const stytchPhone = stytchUser?.phone_numbers.find(
            (item) => item.phone_number.replace(/[^\d+]/g, "") === normalized,
          );
          if (
            stytchPhone &&
            removedPhoneFactorIdRef.current !== stytchPhone.phone_id
          ) {
            await stytch.user.deletePhoneNumber(stytchPhone.phone_id);
            removedPhoneFactorIdRef.current = stytchPhone.phone_id;
          }
          await updatePatient.mutateAsync({ phoneNumber: null });
        },
      });
    } else if (nextPhone !== savedPhone) {
      addPatientTask(t("you.personal_info.phone_label"), {
        phoneNumber: nextPhone,
      });
    }

    if (nextDateOfBirth !== savedDateOfBirth) {
      addPatientTask(t("you.personal_info.birth_date_label"), {
        dateOfBirth: nextDateOfBirth,
      });
    }
    if (draftSexAtBirth !== savedSexAtBirth) {
      addPatientTask(t("you.personal_info.sex_at_birth_label"), {
        gender: draftSexAtBirth,
      });
    }
    if (addressDraft) {
      const existingId = patient.addresses?.[0]?.id;
      addPatientTask(
        t("you.personal_info.address_label"),
        {
          addresses: [
            {
              ...(existingId ? { id: existingId } : {}),
              address1: addressDraft.address1,
              address2: addressDraft.address2,
              city: addressDraft.city,
              postalCode: addressDraft.postalCode,
              country: addressDraft.country,
            },
          ],
        },
        "address",
      );
    }

    if (!tasks.length) return;

    savingRef.current = true;
    setIsSaving(true);
    setSaveIssue(null);

    try {
      const results = await Promise.allSettled(
        tasks.map((task) => task.save()),
      );
      const failedLabels = results.flatMap((result, index) => {
        const task = tasks[index];
        return result.status === "rejected" && task ? [task.label] : [];
      });
      const successfulKeys = new Set(
        results.flatMap((result, index) => {
          const task = tasks[index];
          return result.status === "fulfilled" && task?.key ? [task.key] : [];
        }),
      );

      // Successful fields should not be submitted again if another field
      // failed. React Query has already updated the matching cache value;
      // clear only the local draft markers for those successful operations.
      if (successfulKeys.has("address")) setAddressDraft(null);
      if (successfulKeys.has("email-clear")) setEmailClearRequested(false);
      if (successfulKeys.has("phone-clear")) setPhoneClearRequested(false);

      if (failedLabels.length > 0) {
        setSaveIssue({
          variant: "important",
          message:
            failedLabels.length === tasks.length
              ? t("you.personal_info.save_failed_message")
              : t("you.personal_info.save_partial_message", {
                  params: { fields: failedLabels.join(", ") },
                }),
        });
        return;
      }

      if (emailVerificationFailed) {
        setSaveIssue({
          variant: "warning",
          message: t("you.personal_info.email_verification_send_failed"),
        });
        return;
      }

      onClose();
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    addressDraft,
    currentGoalLabel,
    data,
    dateOfBirth,
    draftSexAtBirth,
    email,
    emailClearRequested,
    firstName,
    hasChanges,
    hasSexAtBirthGoalConflict,
    isSaving,
    lastName,
    onClose,
    patient,
    phone,
    phoneClearRequested,
    savedSexAtBirth,
    stytch.magicLinks.email,
    stytch.user,
    stytchUser,
    t,
    updatePatient,
  ]);

  // Show "Send SMS Invite" button only when ?show_invite_ui=true is in the URL.
  const showInviteUI = useMemo(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return false;
    return (
      new URLSearchParams(window.location.search).get("show_invite_ui") ===
      "true"
    );
  }, []);
  const betaInvite = useSendBetaInvite();

  return (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <ProfileTextField
            label={t("you.personal_info.first_name_label")}
            labelIcon={UserRound}
            value={firstName}
            placeholder={t("you.personal_info.first_name_placeholder")}
            autoComplete="given-name"
            onChangeText={setFirstName}
            disabled={isSaving}
          />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <ProfileTextField
            label={t("you.personal_info.last_name_label")}
            labelIcon={UserRound}
            value={lastName}
            placeholder={t("you.personal_info.last_name_placeholder")}
            autoComplete="family-name"
            onChangeText={setLastName}
            disabled={isSaving}
          />
        </View>
      </View>

      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <MoonDateField
            label={t("you.personal_info.birth_date_label")}
            labelIcon={CalendarDays}
            value={dateOfBirth}
            onChange={setDateOfBirth}
            disabled={isSaving}
          />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <MoonFormField
            label={t("you.personal_info.sex_at_birth_label")}
            labelIcon={VenusAndMars}
          >
            <View
              accessibilityRole="radiogroup"
              accessibilityLabel={t("you.personal_info.sex_at_birth_label")}
              style={{ flexDirection: "row", gap: 8 }}
            >
              {SEX_OPTIONS.map(({ value, labelKey, Icon }) => {
                const isSelected = draftSexAtBirth === value;
                return (
                  <Pressable
                    key={value}
                    accessibilityRole="radio"
                    accessibilityLabel={t(labelKey)}
                    accessibilityHint={
                      savedSexAtBirth && value !== savedSexAtBirth
                        ? t("you.personal_info.sex_at_birth_change_hint")
                        : undefined
                    }
                    accessibilityState={{ checked: isSelected }}
                    aria-checked={isSelected}
                    onPress={() => {
                      capturePress(
                        pressLabel(t(labelKey)),
                        "sex_at_birth_option",
                      );
                      selectSexAtBirth(value);
                    }}
                    disabled={isSaving}
                    style={{
                      flex: 1,
                      minHeight: 44,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? semanticColors.border.strong
                        : semanticColors.border.subtle,
                      backgroundColor: isSelected
                        ? semanticColors.overlay.hover
                        : "rgba(255, 255, 255, 0.5)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      size={21}
                      color={isSelected ? colors.text : colors.textLight}
                      strokeWidth={1.65}
                    />
                  </Pressable>
                );
              })}
            </View>
          </MoonFormField>
        </View>
      </View>

      <View>
        <MoonPhoneField
          testID="personal-info-phone-field"
          label={t("you.personal_info.phone_label")}
          labelIcon={Phone}
          icon={null}
          countrySelectorTestID="personal-info-country-picker"
          countrySearchTestID="personal-info-country-search"
          value={phone}
          disabled={phoneVerified || isSaving}
          onClear={!isSaving && canRemovePhone ? requestClearPhone : undefined}
          onChange={(value) => {
            setPhone(value);
            setPhoneClearRequested(false);
          }}
          right={
            showPhoneVerification && !isSaving
              ? phoneVerification.control
              : undefined
          }
        />
        {showPhoneVerification && phoneVerification.error ? (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 6,
              fontSize: 12,
              lineHeight: 16,
              color: "#B91C1C",
            }}
          >
            {phoneVerification.error}
          </Text>
        ) : null}
      </View>

      <View>
        <ProfileTextField
          label={t("you.personal_info.email_label")}
          labelIcon={Mail}
          value={email}
          placeholder={t("you.personal_info.email_placeholder")}
          keyboardType="email-address"
          autoComplete="email"
          disabled={emailVerified || isSaving}
          onClear={!isSaving && canRemoveEmail ? requestClearEmail : undefined}
          onChangeText={(value) => {
            setEmail(value);
            setEmailClearRequested(false);
            setEmailVerificationError(null);
          }}
          right={
            !emailVerified && emailForVerification && !isSaving ? (
              <EmailVerificationBadge
                status={emailVerificationStatus}
                email={emailForVerification}
                onError={setEmailVerificationError}
              />
            ) : undefined
          }
        />
        {emailVerificationError ? (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 6,
              fontSize: 12,
              lineHeight: 16,
              color: "#B91C1C",
            }}
          >
            {emailVerificationError}
          </Text>
        ) : null}
      </View>

      <AddressField
        label={t("you.personal_info.address_label")}
        labelIcon={MapPin}
        labelHint={t("you.personal_info.address_hint")}
        icon={null}
        value={data.address}
        onChange={setAddressDraft}
        disabled={isSaving}
      />

      {saveIssue ? (
        <ActionBanner
          variant={saveIssue.variant}
          message={saveIssue.message}
          actionLabel={
            saveIssue.action === "review-goal"
              ? t("you.personal_info.sex_at_birth_goal_review_action")
              : undefined
          }
          onAction={
            saveIssue.action === "review-goal"
              ? reviewGoalForSexAtBirthChange
              : undefined
          }
        />
      ) : null}

      {showInviteUI && (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("you.personal_info.invite_send_sms")),
              "you.personal_info.invite_send_sms",
            );
            betaInvite.mutate();
          }}
          disabled={betaInvite.isPending || betaInvite.isSuccess}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            paddingVertical: 14,
            paddingHorizontal: 20,
            borderRadius: 12,
            backgroundColor: betaInvite.isSuccess
              ? "#16a34a"
              : betaInvite.isError
                ? "#dc2626"
                : EMBER.colors.warm,
            opacity: betaInvite.isPending ? 0.7 : 1,
          }}
        >
          {betaInvite.isPending ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Send size={16} color="#fff" />
          )}
          <Text
            className="font-dm-sans"
            style={{ fontSize: 15, fontWeight: "600", color: "#fff" }}
          >
            {betaInvite.isSuccess
              ? t("you.personal_info.invite_sent")
              : betaInvite.isError
                ? t("you.personal_info.invite_failed")
                : t("you.personal_info.invite_send_sms")}
          </Text>
        </Pressable>
      )}

      <View style={{ flexDirection: "row", gap: 10, paddingTop: 10 }}>
        <SoftSecondaryButton
          label={t("you.personal_info.cancel")}
          onPress={onClose}
          disabled={isSaving}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("you.personal_info.save_changes")}
          onPress={handleSave}
          disabled={!hasChanges || isSaving}
          loading={isSaving}
          flex={1}
        />
      </View>

      <Dialog
        visible={pendingSexAtBirth !== null}
        onClose={() => setPendingSexAtBirth(null)}
        layout="centered"
      >
        <Text
          accessibilityRole="header"
          className="font-dm-sans"
          style={{
            fontSize: 20,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
            marginBottom: 10,
          }}
        >
          {t("you.personal_info.sex_at_birth_change_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            lineHeight: 20,
            color: EMBER.colors.textLight,
            textAlign: "center",
            marginBottom: 22,
          }}
        >
          {sexAtBirthChangeDescription}
        </Text>
        <View style={{ flexDirection: "row", gap: 12 }}>
          <SoftSecondaryButton
            label={t("you.personal_info.sex_at_birth_change_cancel")}
            onPress={() => setPendingSexAtBirth(null)}
            flex={1}
          />
          <DialogPrimaryButton
            label={sexAtBirthChangeConfirmLabel}
            onPress={confirmSexAtBirthChangeFromDialog}
            flex={1}
          />
        </View>
      </Dialog>
    </View>
  );
}

function isSexAtBirth(value: string | null): value is SexAtBirth {
  return value === "AFAB" || value === "AMAB";
}

function toNullable(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed || null;
}

function ProfileTextField({
  label,
  labelIcon,
  value,
  placeholder,
  keyboardType,
  autoComplete,
  onChangeText,
  disabled = false,
  right,
  onClear,
}: {
  label: string;
  labelIcon?: React.ComponentProps<typeof MoonFormField>["labelIcon"];
  value: string;
  placeholder: string;
  keyboardType?: React.ComponentProps<typeof TextInput>["keyboardType"];
  autoComplete?: React.ComponentProps<typeof TextInput>["autoComplete"];
  onChangeText: (value: string) => void;
  disabled?: boolean;
  right?: React.ReactNode;
  onClear?: () => void;
}) {
  const capturePress = usePressCapture("ProfileTextField");
  return (
    <MoonFormField label={label} labelIcon={labelIcon} disabled={disabled}>
      <MoonFieldInput>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <TextInput
            className="font-dm-sans"
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={colors.textFaint}
            keyboardType={keyboardType ?? "default"}
            autoComplete={autoComplete}
            editable={!disabled}
            returnKeyType="done"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              color: colors.text,
              paddingVertical: 10,
              ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
            }}
          />
          {right ? <View style={{ flexShrink: 0 }}>{right}</View> : null}
          {onClear && value ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Clear ${label}`}
              onPress={() => {
                capturePress(pressLabel(`Clear ${label}`), "clear_field");
                onClear();
              }}
              hitSlop={8}
              style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: `${colors.textFaint}20`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={11} color={colors.textFaint} strokeWidth={2.5} />
            </Pressable>
          ) : null}
        </View>
      </MoonFieldInput>
    </MoonFormField>
  );
}
